<template>
  <div class="light-reflection-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$θi = θr$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
           <!-- 背景微弱格点 -->
           <defs>
             <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
               <circle cx="1" cy="1" r="0.5" fill="rgba(255,255,255,0.05)" />
             </pattern>
           </defs>
           <rect width="100%" height="100%" fill="url(#grid)" />
           
           <!-- 镜面 -->
           <rect x="50" y="180" width="300" height="6" fill="#1e293b" rx="3" />
           <line x1="50" y1="180" x2="350" y2="180" stroke="#94a3b8" stroke-width="1.5" />
           <rect x="50" y="174" width="300" height="4" fill="rgba(255,255,255,0.08)" />
           
           <!-- 法线 -->
           <line x1="200" y1="20" x2="200" y2="180" stroke="rgba(255,255,255,0.2)" stroke-width="1.5" stroke-dasharray="8,4" />
           <text x="205" y="32" fill="rgba(148, 163, 184, 0.4)" font-size="10" font-weight="700">法线</text>
           
           <!-- 入射光 -->
           <line :x1="sourcePos.x" :y1="sourcePos.y" x2="200" y2="180" stroke="#f59e0b" stroke-width="3" />
           
           <!-- 反射光 -->
           <line x1="200" y1="180" :x2="reflectedEnd.x" :y2="reflectedEnd.y" stroke="#10b981" stroke-width="3" opacity="0.8" />
           
           <!-- 光源图标 -->
           <circle :cx="sourcePos.x" :cy="sourcePos.y" r="8" fill="#1e293b" stroke="#f59e0b" stroke-width="2" />
           <text :x="sourcePos.x - 25" :y="sourcePos.y - 15" fill="#f59e0b" font-size="10" font-weight="700">光源 S</text>

           <!-- 角度标注 -->
           <path :d="anglePathIn" fill="none" stroke="#f59e0b" stroke-width="2" stroke-dasharray="2,1" />
           <text :x="170" y="160" fill="#f59e0b" font-weight="800" font-size="12" text-anchor="end">{{ Math.round(incidentAngle) }}°</text>
           
           <path :d="anglePathOut" fill="none" stroke="#10b981" stroke-width="2" stroke-dasharray="2,1" />
           <text :x="230" y="160" fill="#10b981" font-weight="800" font-size="12" text-anchor="start">{{ Math.round(incidentAngle) }}°</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>光路状态</label>
              <span class="val">镜面反射</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="s-item">
              <label>改变入射位置 (左移光源)</label>
              <el-slider v-model="sliderX" :min="60" :max="195" size="small"></el-slider>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>反射原理：</strong>三线共面、法线居中。入射角 $\theta_i$ 与反射角 $\theta_r$ 恒相等。这也是潜望镜等光学仪器的基础。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LightReflectionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() { return { sliderX: 100 } },
  computed: {
    sourcePos() { return { x: this.sliderX, y: 70 }; },
    incidentAngle() {
       const dx = 200 - this.sourcePos.x;
       const dy = 180 - this.sourcePos.y;
       return Math.atan2(dx, dy) * 180 / Math.PI;
    },
    reflectedEnd() {
       const angleRad = this.incidentAngle * Math.PI / 180;
       return { x: 200 + 200 * Math.sin(angleRad), y: 180 - 200 * Math.cos(angleRad) };
    },
    anglePathIn() {
       const r = 40;
       const startAngle = -Math.PI/2;
       const endAngle = -Math.PI/2 - (this.incidentAngle * Math.PI / 180);
       return this.describeArc(200, 180, r, startAngle, endAngle);
    },
    anglePathOut() {
       const r = 40;
       const startAngle = -Math.PI/2;
       const endAngle = -Math.PI/2 + (this.incidentAngle * Math.PI / 180);
       return this.describeArc(200, 180, r, startAngle, endAngle);
    }
  },
  methods: {
    describeArc(x, y, radius, startAngle, endAngle) {
       const start = { x: x + radius * Math.cos(endAngle), y: y + radius * Math.sin(endAngle) };
       const end = { x: x + radius * Math.cos(startAngle), y: y + radius * Math.sin(startAngle) };
       const arcSweep = Math.abs(endAngle - startAngle) <= Math.PI ? "0" : "1";
       return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${arcSweep} 0 ${end.x} ${end.y}`;
    }
  }
}
</script>

<style scoped lang="scss">
.light-reflection-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #64748b; font-weight: 700; } .val { font-size: 13px; color: #0ea5e9; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-item { label { font-size: 12px; color: #64748b; font-weight: 700; display: block; margin-bottom: 2px; } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
